import { memo, ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { cn } from 'support/helpers/cn/cn';
import { FieldState } from 'support/types/ui';
import { RowStatus } from 'components/DataTable/cells/WebEDI/components/RowStatus/RowStatus';

import { InputInsideCell } from '../InputInsideCell';

type TextCellInputProps = {
  error: any;
  value?: string;
  readOnly: boolean;
  disabled?: boolean;
  isStatusColumn: boolean;
  cellClassNames: string;
  fieldId: string;
  leftAddon: ReactNode | null;
  rightAddon?: ReactNode;
  quantityField: string;
  isAdded: boolean;
  accessorKey: string;
  fieldState?: FieldState;
};
// eslint-disable-next-line react/display-name
export const TextCellInput = memo(
  ({
    error,
    value,
    readOnly,
    disabled,
    isStatusColumn,
    fieldId,
    cellClassNames,
    leftAddon,
    rightAddon,
    quantityField,
    isAdded,
    accessorKey,
    fieldState = 'default',
  }: TextCellInputProps) => {
    const { register } = useFormContext();
    const { t } = useTranslation();

    return (
      <InputInsideCell
        className={cn(
          'size-full rounded-none border-none! bg-transparent p-3 text-start text-xs shadow-none! outline-hidden!',
          {
            'text-blue-700 focus:text-inherit focus-visible:ring-blue-500! focus-visible:border-blue-500!':
              fieldState === 'pending-validation',
            'bg-warning-50': fieldState === 'warning',
          },
          cellClassNames,
          isStatusColumn ? 'pl-10!' : undefined,
        )}
        placeholder={!value && error?.type === 'required' ? t('common:table.errors.requiredField') : undefined}
        title={value ? String(value) : undefined}
        {...(value ? {} : register(fieldId))}
        value={value}
        readOnly={readOnly}
        disabled={disabled}
        type="text"
        fieldState={fieldState}
        leftIcon={
          isStatusColumn ? (
            <RowStatus
              statusColumn={quantityField}
              currentPath={fieldId}
              isProcurosAdded={isAdded}
              currentColumn={accessorKey}
              tooltips={{
                deleted: t('webedi:inputCell.status.deleted'),
                partial: t('webedi:inputCell.status.partial'),
                complete: t('webedi:inputCell.status.completed'),
              }}
            />
          ) : null
        }
        leftAddon={leftAddon}
        rightAddon={rightAddon}
        id={fieldId}
        data-field={fieldId}
        data-testid={fieldId}
        data-1p-ignore
      />
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.fieldId.includes('parties') && prevProps.fieldId.includes('type')) {
      return false;
    }

    return (
      prevProps.value === nextProps.value &&
      prevProps.error === nextProps.error &&
      prevProps.readOnly === nextProps.readOnly &&
      prevProps.disabled === nextProps.disabled &&
      prevProps.isStatusColumn === nextProps.isStatusColumn &&
      prevProps.cellClassNames === nextProps.cellClassNames &&
      prevProps.quantityField === nextProps.quantityField &&
      prevProps.isAdded === nextProps.isAdded &&
      prevProps.accessorKey === nextProps.accessorKey &&
      prevProps.fieldId === nextProps.fieldId &&
      prevProps.fieldState === nextProps.fieldState
    );
  },
);
